import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Modal, 
  Button, 
  Divider, 
  TextField,
  FormHelperText
} from '@mui/material';
import { 
  footerStyle, 
  headerStyle, 
  inputStyle, 
  modalStyle 
} from '../../OrderEntry/styles';
import { NumericFormat } from 'react-number-format';
import { tokens } from '../../../Common/designTokens';

const CounterPriceModal = ({ 
  open, 
  onClose, 
  venueData, 
  onSubmit 
}) => {
  const [counterPrice, setCounterPrice] = useState('');
  const [error, setError] = useState(false);
  const [touched, setTouched] = useState(false);

  // Reset fields when a new venue is selected or modal opens
  useEffect(() => {
    if (venueData && open) {
      setCounterPrice(venueData.counterPrice || '');
      setError(false);
      setTouched(false);
    }
  }, [venueData, open]);

  const handleSubmit = () => {
    // First mark as touched to ensure validation messages show
    setTouched(true);
    
    // Ensure counterPrice is a valid, positive number
    if (counterPrice === '' || counterPrice === null || counterPrice === undefined || 
        isNaN(Number(counterPrice)) || Number(counterPrice) <= 0) {
      setError(true);
      return;
    }
    
    onSubmit({
      ...venueData,
      counterPrice: counterPrice
    });
    onClose();
  };

  // Handle value change with validation
  const handleValueChange = (values) => {
    const { floatValue, formattedValue } = values;
    
    // Only mark as touched if user has actually entered something
    if (formattedValue !== '') {
      setTouched(true);
    }
    
    if (floatValue === undefined || floatValue <= 0) {
      if (formattedValue !== '') { // Only set error if there's actual input
        setError(true);
      } else {
        setError(false);
      }
    } else {
      setError(false);
    }
    
    setCounterPrice(floatValue);
  };

  // Determine if the submit button should be disabled
  // Use strict validation: must be a number > 0
  const isSubmitDisabled = 
    counterPrice === '' || 
    counterPrice === null || 
    counterPrice === undefined || 
    isNaN(Number(counterPrice)) || 
    Number(counterPrice) <= 0 || 
    error;

  if (!venueData) return null;

  return (
    <Modal 
      open={open} 
      onClose={onClose} 
      aria-labelledby="counter-price-modal-title"
    >
      <Box sx={{
        ...modalStyle,
        width: "500px",
        bgcolor: tokens.colors.bodyBackgroundColor,
        border: `1px solid ${tokens.colors.bodyBorderColor50}`,
        borderRadius: "8px",
      }}>
        <Box sx={headerStyle}>
          <Typography 
            id="counter-price-modal-title" 
            variant="h6" 
            fontWeight="bold"
          >
            Enter Counter Price
          </Typography>
        </Box>
        
        {/* Venue Details with Vertical Separators */}
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between', 
          p: '20px',
          bgcolor: tokens.colors.defaultGridRowColor,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 'bold', color: tokens.colors.fontColour }}>
              Venue: {venueData.venue}
            </Typography>
            
            <Divider orientation="vertical" sx={{ mx: 2, backgroundColor: tokens.colors.bodyBorderColor50, height: 20 }} />
            
            <Typography sx={{ fontWeight: 'bold', color: tokens.colors.fontColour }}>
              Qty: {venueData.qty}
            </Typography>
            
            <Divider orientation="vertical" sx={{ mx: 2, backgroundColor: tokens.colors.bodyBorderColor50, height: 20 }} />
            
            <Typography sx={{ fontWeight: 'bold', color: tokens.colors.fontColour }}>
              Current Price: {venueData.price}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ 
          pl: '20px',
          pr: '20px',
          bgcolor: tokens.colors.defaultGridRowColor
        }}>
          {/* Counter Price Input */}
          <NumericFormat
            customInput={TextField}
            autoFocus
            fullWidth
            label="Enter Price"
            value={counterPrice}
            variant="filled"
            placeholder="Enter Price"
            thousandSeparator=","
            allowNegative={false}
            decimalScale={5}
            // Keep required for validation but hide the asterisk
            required
            error={touched && error}
            InputProps={{
              disableUnderline: true
            }}
            InputLabelProps={{
              // This will hide the asterisk
              sx: {
                '& .MuiFormLabel-asterisk': {
                  display: 'none'
                }
              }
            }}
            sx={{
              ...inputStyle,
              '& .MuiInputBase-input::placeholder': {
                color: tokens.colors.fontColour85,
                opacity: 1
              },
              '& .MuiFilledInput-input:-webkit-autofill': {
                WebkitBoxShadow: `0 0 0 1000px ${tokens.colors.white} inset`,
                WebkitTextFillColor: tokens.colors.fontColour
              },
              '& .Mui-error .MuiFilledInput-input': {
                color: tokens.palette.danger
              }
            }}
            onValueChange={handleValueChange}
            onBlur={() => setTouched(true)}
            isAllowed={(values) => {
              const { floatValue } = values;
              if (floatValue === undefined) return true;
              return floatValue <= 150;
            }}
          />
          
          {touched && error && (
            <FormHelperText error sx={{ ml: 1.5, mt: 0.5 }}>
              Please enter a valid price greater than zero
            </FormHelperText>
          )}
          
          <Box sx={{ mt: 3, mx: -3 }}>
            <Divider sx={{ borderColor: tokens.colors.bodyBorderColor50 }} />
          </Box>
          
          <Box sx={{
            ...footerStyle,
            borderTop: "none",
            bgcolor: tokens.colors.defaultGridRowColor,
            "& > :not(style) + :not(style)": {
              ml: 2,
            },
          }}>
            <Button 
              variant="outlined" 
              onClick={onClose} 
              sx={{ 
                textTransform: "none", 
                px: 4, 
                py: '10px',
                color: tokens.colors.fontColour,
                borderColor: tokens.colors.bodyBorderColor50,
                borderRadius: "8px",
                '&:hover': {
                  borderColor: tokens.colors.bodyBorderColor50,
                  backgroundColor: tokens.colors.defaultGridHoverColor,
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              sx={{ 
                textTransform: "none", 
                px: 4, 
                py: '10px',
                backgroundColor: tokens.palette.primary,
                color: tokens.colors.white,
                '&:hover': {
                  backgroundColor: tokens.palette.primaryA80,
                },
                '&.Mui-disabled': {
                  backgroundColor: tokens.colors.disabledBackground,
                  color: tokens.colors.disabledFontColour,
                },
                borderRadius: "8px",
               }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default CounterPriceModal;
